import type { IDemoLocation } from '@/interfaces/demoBusinessTypes';

export const DEMO_LOCATION_MESSAGE_SOURCE = 'omceanbooking-website-demo';

/**
 * Single website contract for the nightly-seeded Demoapt (_89) showcase.
 * Location ids are explicit because the iframe switches the real customer app
 * to these records. Descriptive copy lives beside each id so it cannot silently
 * fall back to a different industry when the mapping changes.
 */
export const DEMO_LOCATIONS: readonly IDemoLocation[] = [
  {
    key: 'acupuncture',
    businessTypeId: 'acupuncture',
    businessTypeName: { en: 'Acupuncture', zh: '針灸' },
    emoji: '🌿',
    locationId: 6,
    bookingPage: 'appointment',
    location: 'Meridian Acupuncture Clinic',
    headline: {
      en: 'Private care plans, practitioners and treatment times in one flow.',
      zh: '療程計畫、醫師與時段，在同一流程中完成。',
    },
    points: [
      {
        en: 'New and returning clients see the right visit types.',
        zh: '初診與複診顧客都能找到合適療程。',
      },
      {
        en: 'Practitioner availability controls every appointment.',
        zh: '每個預約都會同步確認醫師時段。',
      },
      {
        en: 'Care plans make multi-visit treatment easy to purchase.',
        zh: '多次療程方案可直接選購。',
      },
    ],
  },
  {
    key: 'badminton',
    businessTypeId: 'badminton',
    businessTypeName: { en: 'Badminton', zh: '羽毛球' },
    emoji: '🏸',
    locationId: 1,
    bookingPage: 'appointment',
    location: 'Ace Badminton Center',
    headline: {
      en: 'Courts, coaches and equipment in one booking.',
      zh: '場地、教練與器材一次完成預約。',
    },
    points: [
      {
        en: 'Customers choose the court and time that suit their group.',
        zh: '顧客可依同行人數選擇合適場地與時段。',
      },
      {
        en: 'Coach and equipment availability are checked together.',
        zh: '預約時同步檢查教練與器材空檔。',
      },
      {
        en: 'Capacity rules prevent overlapping or oversized bookings.',
        zh: '容量規則避免重複或超額預約。',
      },
    ],
  },
  {
    key: 'ball-swimming',
    businessTypeId: 'ball-swimming',
    businessTypeName: { en: 'Aquatic Therapy', zh: '水療' },
    emoji: '🏊',
    locationId: 7,
    bookingPage: 'appointment',
    location: 'Blue Current Aquatic Therapy',
    headline: {
      en: 'Make every recovery session safe, personal and easy to book.',
      zh: '讓每次水中恢復都安全、個人化且好預約。',
    },
    points: [
      {
        en: 'Clients can compare assessments, private care and small groups.',
        zh: '顧客可比較評估、私人療程與小班課。',
      },
      {
        en: 'Therapist and warm-pool capacity stay coordinated.',
        zh: '治療師與溫水池容量保持同步。',
      },
      {
        en: 'Recovery packs support a consistent care journey.',
        zh: '恢復套票讓療程更連貫。',
      },
    ],
  },
  {
    key: 'beauty',
    businessTypeId: 'beauty',
    businessTypeName: { en: 'Beauty', zh: '美容' },
    emoji: '💅',
    locationId: 5,
    bookingPage: 'appointment',
    location: 'Polished Beauty Lounge',
    headline: {
      en: 'Build a polished journey from consultation to follow-up.',
      zh: '從諮詢到後續關懷，打造完整美容體驗。',
    },
    points: [
      {
        en: 'Customers browse treatments by category and specialist.',
        zh: '顧客可依療程分類與美容師瀏覽服務。',
      },
      {
        en: 'Required rooms and equipment are reserved automatically.',
        zh: '所需空間與器材會自動保留。',
      },
      {
        en: 'Intake details stay attached to the customer record.',
        zh: '諮詢資料會保留在顧客紀錄中。',
      },
    ],
  },
  {
    key: 'dentists',
    businessTypeId: 'dentists',
    businessTypeName: { en: 'Dentists', zh: '牙醫診所' },
    emoji: '🦷',
    locationId: 8,
    bookingPage: 'appointment',
    location: 'Harbor Dental Studio',
    headline: {
      en: 'Clear treatment choices and calmer patient scheduling.',
      zh: '療程選擇清楚，病患排程更安心。',
    },
    points: [
      {
        en: 'Patients choose check-ups, whitening or urgent assessments.',
        zh: '病患可選擇檢查、美白或緊急評估。',
      },
      {
        en: 'Dentist and treatment-room availability are linked.',
        zh: '醫師與診療室時段同步連動。',
      },
      {
        en: 'Preventive plans are visible during the same booking journey.',
        zh: '同一預約流程即可查看預防保健方案。',
      },
    ],
  },
  {
    key: 'dog-wash',
    businessTypeId: 'dog-wash',
    businessTypeName: { en: 'Dog Wash', zh: '寵物洗澡' },
    emoji: '🐕',
    locationId: 19,
    bookingPage: 'appointment',
    location: 'Sudsy Paws Dog Wash',
    headline: {
      en: 'The right wash, add-ons and groomer for every dog.',
      zh: '依狗狗體型安排洗澡、加購與美容師。',
    },
    points: [
      {
        en: 'Pet parents choose a small or large dog wash with clear prices.',
        zh: '毛孩家長可依體型選擇小型犬或大型犬洗澡，價格清楚。',
      },
      {
        en: 'Nail trims, ear cleaning and styling are added in the same booking.',
        zh: '剪指甲、清耳朵與造型吹整可在同一筆預約加購。',
      },
      {
        en: 'Wash bays and groomers are checked together for every slot.',
        zh: '每個時段都會同步確認洗澡區與美容師。',
      },
    ],
  },
  {
    key: 'education-training',
    businessTypeId: 'education-training',
    businessTypeName: { en: 'Education & Training', zh: '教育培訓' },
    emoji: '🎓',
    locationId: 9,
    bookingPage: 'appointment',
    location: 'BrightPath Learning Academy',
    headline: {
      en: 'Private tutoring and group learning, organised together.',
      zh: '私人家教與團體課程，集中管理。',
    },
    points: [
      {
        en: 'Students browse subjects, formats and upcoming classes.',
        zh: '學生可瀏覽科目、形式與即將開始的課程。',
      },
      {
        en: 'Tutor schedules keep private sessions conflict-free.',
        zh: '家教時段安排避免私人課程衝突。',
      },
      {
        en: 'Term packs make repeat learning simple to manage.',
        zh: '學期套票讓持續學習更容易管理。',
      },
    ],
  },
  {
    key: 'fitness',
    businessTypeId: 'fitness',
    businessTypeName: { en: 'Fitness Studios', zh: '健身工作室' },
    emoji: '🏋️',
    locationId: 10,
    bookingPage: 'calendar',
    location: 'Elevate Fitness Studio',
    headline: {
      en: 'Classes, assessments and memberships built for momentum.',
      zh: '團課、評估與會員方案，幫助持續進步。',
    },
    points: [
      {
        en: 'Live capacity is shown for strength and HIIT classes.',
        zh: '肌力與間歇課程即時顯示剩餘名額。',
      },
      {
        en: 'Private assessments connect customers with a coach.',
        zh: '私人評估可直接安排合適教練。',
      },
      {
        en: 'Class packs and unlimited memberships are ready to buy.',
        zh: '堂數套票與不限次會員可直接購買。',
      },
    ],
  },
  {
    key: 'free-diving',
    businessTypeId: 'free-diving',
    businessTypeName: { en: 'Free Diving', zh: '自由潛水' },
    emoji: '🤿',
    locationId: 11,
    bookingPage: 'calendar',
    location: 'Deep Blue Freediving',
    headline: {
      en: 'From first breath-hold to depth training, safely scheduled.',
      zh: '從初次閉氣到深度訓練，安全安排每一步。',
    },
    points: [
      {
        en: 'Divers compare discovery, pool and depth sessions.',
        zh: '學員可比較體驗、泳池與深度課程。',
      },
      {
        en: 'Coach and group limits protect every training slot.',
        zh: '教練與人數限制保障每個訓練時段。',
      },
      {
        en: 'Progress packs keep regular practice easy.',
        zh: '進階套票讓固定練習更簡單。',
      },
    ],
  },
  {
    key: 'hairdressers',
    businessTypeId: 'hairdressers',
    businessTypeName: { en: 'Hairdressers', zh: '美髮造型' },
    emoji: '💇',
    locationId: 2,
    bookingPage: 'appointment',
    location: 'Blow Bar Lane',
    headline: {
      en: 'Clients pick the stylist, not just the time.',
      zh: '顧客不只選時間，也能選擇設計師。',
    },
    points: [
      {
        en: 'Per-stylist availability keeps every appointment with the right person.',
        zh: '依設計師顯示可預約時段，安排更精準。',
      },
      {
        en: 'Service durations, add-ons and prices stay clear during booking.',
        zh: '預約時清楚顯示服務時長、加購項目與價格。',
      },
      {
        en: 'Customer notes and visit history remain connected to the account.',
        zh: '顧客備註與到訪紀錄會保留在帳號中。',
      },
    ],
  },
  {
    key: 'martial-arts',
    businessTypeId: 'martial-arts',
    businessTypeName: { en: 'Martial Arts', zh: '武術格鬥' },
    emoji: '🥋',
    locationId: 12,
    bookingPage: 'calendar',
    location: 'Forge Martial Arts Academy',
    headline: {
      en: 'A clear path from first class to committed member.',
      zh: '從第一堂體驗到長期會員，都有清楚路徑。',
    },
    points: [
      {
        en: 'Adults and children find the right discipline and level.',
        zh: '成人與兒童都能找到合適項目與程度。',
      },
      {
        en: 'Mat capacity keeps every class safe and comfortable.',
        zh: '場地容量讓每堂課安全又舒適。',
      },
      {
        en: 'Flexible packs work across Muay Thai and BJJ.',
        zh: '彈性套票可跨泰拳與柔術使用。',
      },
    ],
  },
  {
    key: 'massage',
    businessTypeId: 'massage',
    businessTypeName: { en: 'Massage', zh: '按摩' },
    emoji: '💆',
    locationId: 3,
    bookingPage: 'appointment',
    location: 'Serenity Massage Spa',
    headline: {
      en: 'Match every treatment with the right room and therapist.',
      zh: '為每項療程安排合適的空間與療癒師。',
    },
    points: [
      {
        en: 'Treatment length controls the available start times automatically.',
        zh: '系統依療程時長自動計算可預約時間。',
      },
      {
        en: 'Rooms, therapists and equipment are checked together.',
        zh: '同步確認空間、療癒師與器材。',
      },
      {
        en: 'Packages and add-ons are offered in the same booking flow.',
        zh: '同一預約流程中即可選購方案與加購項目。',
      },
    ],
  },
  {
    key: 'personal-trainer',
    businessTypeId: 'personal-trainer',
    businessTypeName: { en: 'Personal Trainer', zh: '私人教練' },
    emoji: '🏃',
    locationId: 13,
    bookingPage: 'appointment',
    location: 'Apex Personal Training',
    headline: {
      en: 'Personal goals deserve a genuinely personal booking journey.',
      zh: '個人目標，值得真正個人化的預約體驗。',
    },
    points: [
      {
        en: 'Clients start with an assessment or choose their coach.',
        zh: '顧客可先做評估或直接選擇教練。',
      },
      {
        en: 'Private, couples and small-group formats are clearly priced.',
        zh: '私人、雙人與小班課程價格清楚。',
      },
      {
        en: 'Coaching plans make long-term progress easy to commit to.',
        zh: '教練方案讓長期進步更容易開始。',
      },
    ],
  },
  {
    key: 'pilates',
    businessTypeId: 'pilates',
    businessTypeName: { en: 'Pilates', zh: '皮拉提斯' },
    emoji: '🧘',
    locationId: 14,
    bookingPage: 'calendar',
    location: 'Align Reformer Pilates',
    headline: {
      en: 'The right apparatus, instructor and class size every time.',
      zh: '每次都安排合適器械、老師與課堂人數。',
    },
    points: [
      {
        en: 'Customers compare reformer, tower and prenatal classes.',
        zh: '顧客可比較核心床、塔架與孕期課程。',
      },
      {
        en: 'Small-group capacity reflects the real studio equipment.',
        zh: '小班名額與教室實際器械數量一致。',
      },
      {
        en: 'Private sessions and class packs share one checkout.',
        zh: '私人課與團課套票使用同一結帳流程。',
      },
    ],
  },
  {
    key: 'restaurant',
    businessTypeId: 'restaurant',
    businessTypeName: { en: 'Restaurants', zh: '餐廳' },
    emoji: '🍽️',
    locationId: 4,
    bookingPage: 'appointment',
    location: 'The Golden Fork',
    headline: {
      en: 'Match every party with the right table and dining experience.',
      zh: '為每組客人安排合適餐桌與用餐體驗。',
    },
    points: [
      {
        en: 'Guests choose a dining experience and see live reservation times.',
        zh: '客人可選擇用餐體驗並查看即時預約時段。',
      },
      {
        en: 'Table size and dining-area capacity are assigned automatically.',
        zh: '系統會依人數自動安排餐桌與用餐區容量。',
      },
      {
        en: 'Add-ons and special dining requests stay with the reservation.',
        zh: '加購項目與特別用餐需求會保留在預約中。',
      },
    ],
  },
  {
    key: 'swimming',
    businessTypeId: 'swimming',
    businessTypeName: { en: 'Swimming', zh: '游泳' },
    emoji: '🏊‍♀️',
    locationId: 15,
    bookingPage: 'calendar',
    location: 'Tideway Swim School',
    headline: {
      en: 'The right lesson, lane and coach for every swimmer.',
      zh: '為每位學員安排合適課程、水道與教練。',
    },
    points: [
      {
        en: 'Adults, children and technique swimmers see distinct programs.',
        zh: '成人、兒童與泳姿進階都有專屬課程。',
      },
      {
        en: 'Pool capacity and instructor availability stay aligned.',
        zh: '泳池容量與教練時段保持同步。',
      },
      {
        en: 'Lesson packs support a consistent weekly routine.',
        zh: '課程套票幫助建立穩定每週練習。',
      },
    ],
  },
  {
    key: 'tennis',
    businessTypeId: 'tennis',
    businessTypeName: { en: 'Tennis', zh: '網球' },
    emoji: '🎾',
    locationId: 16,
    bookingPage: 'calendar',
    location: 'Rally Point Tennis Club',
    headline: {
      en: 'Coaches, courts and clinics without scheduling collisions.',
      zh: '教練、球場與團訓，不再發生排程衝突。',
    },
    points: [
      {
        en: 'Players choose private lessons or level-based clinics.',
        zh: '球員可選擇私人課或分級團訓。',
      },
      {
        en: 'Court and coach availability are reserved together.',
        zh: '球場與教練時段會一起保留。',
      },
      {
        en: 'Progress packs work across adult, junior and cardio tennis.',
        zh: '進階套票可用於成人、青少年與有氧網球。',
      },
    ],
  },
  {
    key: 'horse-riding',
    businessTypeId: 'horse-riding',
    businessTypeName: { en: 'Horse Riding', zh: '馬術' },
    emoji: '🐴',
    locationId: 21,
    bookingPage: 'calendar',
    location: 'Blue Ridge Equestrian Centre',
    headline: {
      en: 'Coaches, arenas and horses booked together, every lesson.',
      zh: '每堂課都同時排定教練、馬場與馬匹。',
    },
    points: [
      {
        en: 'Riders book private lessons, group lessons or guided hacks.',
        zh: '騎士可預約私人課、團體課或野外騎乘。',
      },
      {
        en: 'Each horse is reserved like equipment, so none is double-booked.',
        zh: '每匹馬如同設備般被保留，不會重複排課。',
      },
      {
        en: 'Parents book pony club lessons for their children as dependents.',
        zh: '家長可為附屬成員的孩子預約青少年騎士課程。',
      },
    ],
  },
  {
    key: 'wellness-spa',
    businessTypeId: 'wellness-spa',
    businessTypeName: { en: 'Wellness Spa', zh: '健康水療' },
    emoji: '🧖',
    locationId: 17,
    bookingPage: 'appointment',
    location: 'Stillwater Wellness Spa',
    headline: {
      en: 'Treatments, spaces and practitioners stay in sync.',
      zh: '療程、空間與服務人員保持同步。',
    },
    points: [
      {
        en: 'Customers compare rituals, duration and prices before choosing.',
        zh: '顧客可先比較療程、時間與價格再選擇。',
      },
      {
        en: 'Practitioner and treatment-space availability are checked together.',
        zh: '同步確認服務人員與療癒空間的可用時段。',
      },
      {
        en: 'Multi-visit journeys and memberships are ready to purchase.',
        zh: '多次療程與會員方案都可直接購買。',
      },
    ],
  },
  {
    key: 'yoga-dance',
    businessTypeId: 'yoga-dance',
    businessTypeName: { en: 'Yoga & Dance', zh: '瑜珈舞蹈' },
    emoji: '💃',
    locationId: 18,
    bookingPage: 'calendar',
    location: 'Rhythm and Flow Yoga Dance',
    headline: {
      en: 'Yoga, dance and private practice under one welcoming roof.',
      zh: '瑜伽、舞蹈與私人練習，共享友善空間。',
    },
    points: [
      {
        en: 'Students browse movement styles and live class capacity.',
        zh: '學員可瀏覽不同風格與即時課堂名額。',
      },
      {
        en: 'Studio and instructor schedules stay coordinated.',
        zh: '教室與老師時段保持同步。',
      },
      {
        en: 'Flexible packs invite customers to explore every format.',
        zh: '彈性套票鼓勵顧客探索各種課程。',
      },
    ],
  },
  {
    key: 'pole-studio',
    businessTypeId: 'pole-studio',
    businessTypeName: { en: 'Pole Studio', zh: '鋼管教室' },
    emoji: '💫',
    locationId: 20,
    bookingPage: 'calendar',
    location: 'Velvet Spin Pole Studio',
    headline: {
      en: 'Spin, heels and flexibility classes with a pole for every student.',
      zh: '旋轉管、高跟鞋與柔軟度課程，每位學員都有專屬鋼管。',
    },
    points: [
      {
        en: 'Students book foundations, spin, tricks and heels classes by level.',
        zh: '學員依程度預約入門、旋轉管、技巧與高跟鞋課程。',
      },
      {
        en: 'Every place comes with its own pole, so classes never overfill.',
        zh: '每個名額都配有專屬鋼管，課程不會超收。',
      },
      {
        en: 'Class packs, an unlimited monthly pass and private lessons.',
        zh: '課程套票、無限月卡與私人課，一次到位。',
      },
    ],
  },
];

export const DEMO_LOCATION_ID_BY_BUSINESS_TYPE: Readonly<
  Record<string, number>
> = Object.fromEntries(
  DEMO_LOCATIONS.map(location => [location.businessTypeId, location.locationId])
);

export function demoLocationIdForBusinessType(businessTypeId: string): number {
  return DEMO_LOCATION_ID_BY_BUSINESS_TYPE[businessTypeId] ?? 1;
}

export function demoLocationForBusinessType(
  businessTypeId: string
): IDemoLocation | undefined {
  return DEMO_LOCATIONS.find(
    location => location.businessTypeId === businessTypeId
  );
}
